/**
 * Uploads usage events to the central `usage_events` table so the Admin
 * Dashboard can see every login's activity (see supabase/migration_usage_events.sql).
 *
 * Offline-first: events are queued in localStorage and sent in small batches
 * when there is a signed-in session and a connection. Nothing here may ever
 * get in the way of using the app, so every failure is swallowed and retried.
 */
import { supabase as defaultClient } from './supabase';

export const OUTBOX_KEY = 'sleekHearUsageOutbox';
export const MAX_QUEUED = 500;        // oldest are dropped beyond this (long offline stretches)
const BATCH = 100;
const FLUSH_DELAY_MS = 5000;
const RETRY_MS = 60000;
const COALESCE_MS = 60000;
const COALESCED = new Set(['settings_changed']); // fires on every slider tick: keep one a minute
const MAX_STRING = 200;
const KINDS = ['event', 'activity', 'session'];

// Keep rows small and plain: short strings, no nested surprises.
export function sanitizeData(value, depth = 0) {
  if (value === null || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.length > MAX_STRING ? `${value.slice(0, MAX_STRING)}…` : value;
  if (depth >= 2) return undefined;
  if (Array.isArray(value)) return value.slice(0, 20).map(v => sanitizeData(v, depth + 1)).filter(v => v !== undefined);
  if (value && typeof value === 'object') {
    const out = {};
    Object.entries(value).slice(0, 20).forEach(([k, v]) => {
      const clean = sanitizeData(v, depth + 1);
      if (clean !== undefined) out[k.slice(0, 64)] = clean;
    });
    return out;
  }
  return undefined;
}

export class UsageSync {
  constructor({ client, storage } = {}) {
    this._client = client;
    this._storage = storage;
    this.flushing = false;
    this.timer = null;
    this.started = false;
    this.lastCoalesced = new Map();
  }

  get client() { return this._client === undefined ? defaultClient : this._client; }
  get storage() { return this._storage || window.localStorage; }
  get online() { return typeof navigator === 'undefined' || navigator.onLine !== false; }

  read() {
    try {
      const list = JSON.parse(this.storage.getItem(OUTBOX_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  write(list) {
    try {
      this.storage.setItem(OUTBOX_KEY, JSON.stringify(list.slice(-MAX_QUEUED)));
    } catch {
      // storage full or unavailable: the event is simply lost
    }
  }

  /** Queue one event. kind: 'event' | 'activity' | 'session'. Never throws. */
  enqueue(kind, name, data = {}, sessionId = null, now = Date.now()) {
    try {
      if (!this.client || !KINDS.includes(kind) || !name) return;
      if (COALESCED.has(name)) {
        const last = this.lastCoalesced.get(name);
        if (last !== undefined && now - last < COALESCE_MS) return;
        this.lastCoalesced.set(name, now);
      }
      const row = {
        kind,
        name: String(name).slice(0, 64),
        session_id: sessionId ? String(sessionId).slice(0, 64) : null,
        data: sanitizeData(data) || {},
        created_at: new Date(now).toISOString(),
      };
      this.write([...this.read(), row]);
      this.ensureStarted();
      this.schedule();
    } catch {
      // analytics must never break the app
    }
  }

  schedule(delay = FLUSH_DELAY_MS) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flush(), delay);
  }

  ensureStarted() {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;
    window.addEventListener('online', () => this.flush());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush();
    });
    window.addEventListener('pagehide', () => this.flush());
  }

  /** Send queued events. Resolves true when the queue is empty afterwards. */
  async flush() {
    if (this.flushing || !this.client || !this.online) return false;
    this.flushing = true;
    try {
      const { data } = await this.client.auth.getSession();
      if (!data || !data.session) return false; // signed out: keep them for the next login
      for (let pass = 0; pass < 5; pass++) {
        const batch = this.read().slice(0, BATCH);
        if (batch.length === 0) return true;
        const { error } = await this.client.from('usage_events').insert(batch);
        if (error) { this.schedule(RETRY_MS); return false; }
        this.write(this.read().slice(batch.length)); // new events were only ever appended
      }
      if (this.read().length > 0) this.schedule(1000);
      return this.read().length === 0;
    } catch {
      this.schedule(RETRY_MS);
      return false;
    } finally {
      this.flushing = false;
    }
  }
}

const usageSync = new UsageSync();
export default usageSync;
